import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-anxiety",
  title: "A prayer for anxiety — words for when your chest is tight",
  h1: "A prayer for anxiety",
  description:
    "A short prayer to say when anxiety has already started, plus four passages that do not tell you to calm down, and what prayer is not for.",
  targets: [
    "prayer for anxiety",
    "anxiety prayer",
    "what to pray when anxious",
    "prayer for anxious thoughts",
  ],
  published: "2026-09-03",
  standfirst:
    "Anxiety does not wait until you have the right words. This is a prayer for the middle of it, not for afterwards.",
  takeaways: [
    "Say the thing you are actually afraid of, in plain words. Prayer that stays general keeps the fear general, and a general fear cannot be answered.",
    "Philippians 4:6 asks for requests, not for calm. The peace comes after the asking, and it guards rather than removes.",
    "If the anxiety is constant, or it has a physical shape you cannot talk down, this prayer is not a substitute for a doctor. Pray it and make the appointment.",
  ],
  when: [
    "At two in the morning, when the thought has come back for the fourth time.",
    "Before a conversation you have already had eleven times in your head.",
    "In the car park, before you go in.",
    "After you have checked your phone for a reassurance that is not there.",
  ],
  prayer: [
    "God, I am afraid, and I am going to say what of. It is this. I have turned it over until the turning is its own problem, and I still do not know how it ends.",
    "I am not asking you to make me calm. I am asking you to hold the thing itself while I stop holding it, because my hands have been shaking for hours and they are not doing it any good.",
    "You know what I need before I ask. I am asking anyway, because saying it out loud to someone is the part I have been avoiding.",
    "Give me the next hour. Not the whole outcome — the next hour, and something to do in it that is not refreshing a screen.",
    "And if the fear comes back tonight, come back with it.",
  ],
  short: "God, I am afraid of this. Hold it. Give me the next hour.",
  honest: [
    "This prayer does not ask for the fear to stop. It asks for it to be carried, which is a different request and the one Scripture actually models — the psalms of fear almost never end with the danger removed.",
    "Praying it will not settle a body that is running on adrenaline. Nothing said in words does that quickly. Breathing does, walking does, and sometimes medicine does, and none of those are a failure of faith.",
    "Anxiety that will not lift, or that arrives without a subject, is a medical thing as well as a spiritual one. A page of verses is not a diagnosis and this one is not pretending to be.",
  ],
  verses: [
    {
      verse: {
        text: "In nothing be anxious, but in everything, by prayer and petition with thanksgiving, let your requests be made known to God. And the peace of God, which surpasses all understanding, will guard your hearts and your thoughts in Christ Jesus.",
        ref: "Philippians 4:6–7",
      },
      note: "The verse everyone quotes at anxious people, and it is worth reading slowly enough to notice what it does not say. It does not say be calm. It says make the request — specifically, out loud, item by item — and the peace arrives afterwards as a guard posted around your thinking, not as the removal of the thing you were afraid of.",
    },
    {
      verse: {
        text: "casting all your worries on him, because he cares for you.",
        ref: "1 Peter 5:7",
      },
      note: "Written to people under real persecution, not to people having a difficult week. The verb is violent — throw it, the way you throw something you have decided to stop carrying — and the reason given is not that the worry was silly. It is that someone else is willing to take it.",
    },
    {
      verse: {
        text: "In the multitude of my thoughts within me, your comforts delight my soul.",
        ref: "Psalm 94:19",
      },
      note: "The most accurate description of an anxious mind in the Bible: not one fear but a multitude, arriving together. The psalm does not ask for fewer thoughts. It reports that comfort found its way in anyway, while the multitude was still going.",
    },
    {
      verse: {
        text: "Therefore don't be anxious for tomorrow, for tomorrow will be anxious for itself. Each day's own evil is sufficient.",
        ref: "Matthew 6:34",
      },
      note: "Often read as reassurance that tomorrow will be fine. It says the opposite — tomorrow will have its own trouble, and today already has enough. The comfort is in the size of the unit you are being asked to carry, which is one day, and not one day plus every rehearsal of the next.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Say the actual thing",
      id: "say-the-actual-thing",
    },
    {
      type: "p",
      text: "Most anxious prayer is vague on purpose. Naming the fear makes it real, and while it stays a fog it still feels survivable. But a fog cannot be answered, and it cannot be argued with either — which is why the same anxious prayer can be said for a week without anything moving.",
    },
    {
      type: "p",
      text: "The psalms are specific to the point of embarrassment. They name the enemy, the debt, the illness, the friend who left. That specificity is not a literary style; it is the mechanism. A named fear has edges, and a thing with edges is a thing you can hand over.",
    },
    {
      type: "h2",
      text: "What to do with the phone",
      id: "what-to-do-with-the-phone",
    },
    {
      type: "p",
      text: "Anxiety and a phone are a closed loop. The fear produces the urge to check, the check produces nothing conclusive, the inconclusive result produces more fear, and forty minutes disappear. If you are praying this {at night}, the honest first step is to put the phone somewhere you would have to stand up to reach, and the second is to say the prayer before you have decided whether it worked.",
      links: {
        "at night": "/blog/how-to-stop-scrolling-at-night",
      },
    },
    {
      type: "note",
      text: "Bible Shield exists for exactly this loop: the apps stay shut until you have prayed. But the loop breaks the same way without it — the point is the gap between the urge and the opening, and anything that widens the gap will do.",
    },
    {
      type: "h2",
      text: "When it does not lift",
      id: "when-it-does-not-lift",
    },
    {
      type: "p",
      text: "Sometimes you pray and nothing changes. That is not evidence that the prayer was wrong or that you did it badly. Half the psalter is written by people whose situation did not improve, and the psalms are still in the book — which is the strongest available argument that unanswered prayer belongs inside faith rather than outside it.",
    },
    {
      type: "p",
      text: "And if it is constant, if it wakes you, if it has taken your appetite: tell a doctor. Anxiety is a thing bodies do, and treating it is not a lack of trust. Pray the prayer and make the appointment; nothing in Scripture asks you to choose.",
    },
  ],
  faq: [
    {
      q: "What is the best short prayer for anxiety?",
      a: 'One sentence that names the fear: "God, I am afraid of this — hold it, and give me the next hour." Short enough to say while your heart is racing, specific enough to be a real request rather than a mood.',
    },
    {
      q: "Which Bible verse helps most with anxiety?",
      a: "Philippians 4:6–7 is the one most people are given, and it is genuinely the right one — as long as it is read as an instruction to ask for specific things, not an instruction to feel calm.",
    },
    {
      q: "Is it wrong to take medication for anxiety if I am praying about it?",
      a: "No. Scripture never sets treatment and prayer against each other, and the choice is one the text does not ask you to make. Pray, and see a doctor.",
    },
  ],
  relatedPrayers: ["prayer-before-bed", "prayer-when-you-cant-pray"],
  related: ["how-to-stop-scrolling-at-night", "phone-addiction-bible-verses"],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-peace",
  ],
  relatedPassages: ["philippians-4-6-7", "isaiah-41-10"],
};
